import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { exchangeLink } from "../api";
import { BuildInfo } from "../components/build-info";
import {
  BonusRewardStatus,
  JokerStatus,
  LivesStatus,
  RankSpotlight,
  ScoreStatus,
  rankValue,
} from "../components/game-status";
import { RoomImage, SharedAudio } from "../components/media";
import { QuizBoard } from "../components/quiz-board";
import { RankLadder } from "../components/rank-ladder";
import { ConnectionStatus, ErrorBanner } from "../components/status";
import { Timer } from "../components/timer";
import { useRoomConnection } from "../hooks/use-room-connection";
import { loadSession, storeSession } from "../session";
import { answerPresentationClasses, useFiftyFiftyComplete } from "../question-presentation";

function releasedAnswerText(
  projection: NonNullable<ReturnType<typeof useRoomConnection>["projection"]>,
  optionIds: string[],
  text?: string,
): string {
  if (text) return text;
  return optionIds
    .map((optionId) => projection.question?.answers.find((answer) => answer.optionId === optionId)?.text ?? optionId)
    .join(", ");
}

export function StreamPage() {
  const { linkId } = useParams();
  const [roomId, setRoomId] = useState<string>();
  const [token, setToken] = useState<string>();
  const [exchangeError, setExchangeError] = useState<string>();

  useEffect(() => {
    if (!linkId) return;
    const storedRoomId = sessionStorage.getItem(`quizshow-stream-link:${linkId}`);
    const stored = storedRoomId ? loadSession(storedRoomId, "stream") : undefined;
    if (stored) {
      setRoomId(stored.roomId);
      setToken(stored.token);
      return;
    }
    const secret = new URLSearchParams(window.location.hash.slice(1)).get("key");
    if (!secret) {
      setExchangeError("Stream-Link enthält kein gültiges Geheimnis.");
      return;
    }
    void exchangeLink(linkId, secret)
      .then((response) => {
        storeSession({ roomId: response.roomId, token: response.token, role: "stream" });
        sessionStorage.setItem(`quizshow-stream-link:${linkId}`, response.roomId);
        setRoomId(response.roomId);
        setToken(response.token);
      })
      .catch((error) => setExchangeError(error instanceof Error ? error.message : "Stream-Link ist ungültig."));
  }, [linkId]);

  const { projection, status, error } = useRoomConnection(roomId, token);
  const fiftyFiftyComplete = useFiftyFiftyComplete(projection?.fiftyFiftyAnimation);
  const winner = useMemo(
    () => projection?.players.find((player) => player.playerId === projection.buzzer.winner?.playerId),
    [projection],
  );
  const rankLeader = useMemo(() => {
    if (!projection?.ranks.length) return undefined;
    return [...(projection.teams.length ? projection.teams : projection.players)]
      .sort(
        (left, right) =>
          rankValue(projection, right.score, right.rankProgress) -
          rankValue(projection, left.score, left.rankProgress),
      )[0];
  }, [projection]);

  return (
    <main
      className="stream-shell"
      style={
        {
          "--quiz-primary": projection?.theme.primaryColor ?? "#8f1838",
          "--quiz-accent": projection?.theme.accentColor ?? "#ef4565",
          "--quiz-background": projection?.theme.backgroundColor ?? "#09070a",
        } as React.CSSProperties
      }
    >
      <div className="stream-status">
        <ConnectionStatus status={status} />
      </div>
      <ErrorBanner message={exchangeError ?? error} />
      <SharedAudio roomId={roomId} token={token} mediaId={projection?.audio.mediaId} playing={projection?.audio.status === "playing"} />
      {projection ? (
        <>
          <header className="stream-header">
            <div>
              <span className="eyebrow">Quizshow Studio</span>
              <h1>{projection.quizTitle}</h1>
            </div>
            {projection.question?.timerEnabled || projection.timer.status === "running" ? (
              <Timer timer={projection.timer} />
            ) : null}
          </header>
          <div className="stream-stage-layout">
            <section
              className="stream-stage question-stage"
              style={{ backgroundColor: projection.question?.media.backgroundColor }}
            >
            {projection.question?.media.backgroundMediaId ? (
              <RoomImage
                roomId={roomId}
                token={token}
                mediaId={projection.question.media.backgroundMediaId}
                className="question-background"
              />
            ) : null}
            <div className="stage-card__content">
              {projection.status === "ended" ? (
                <StreamEnd projection={projection} />
              ) : projection.moduleId === "quizboard" && projection.board && !projection.question ? (
                <QuizBoard board={projection.board} />
              ) : projection.question ? (
                <>
                  <span className="eyebrow">
                    {projection.moduleId === "quizboard"
                      ? `${projection.question.points} Punkte`
                      : `Frage ${projection.currentQuestionIndex + 1}`}
                  </span>
                  {projection.question.questionType === "bonus" ? <span className="badge">Bonusfrage</span> : null}
                  {projection.activePointMultiplier === 2 ? <span className="badge">Doppelte Wertung</span> : null}
                  <BonusRewardStatus projection={projection} />
                  {projection.solutionOptionIds.length ? (
                    <div className="solution-banner">
                      {projection.questionResolution.resolved
                        ? "Frage beendet – Lösung wird angezeigt"
                        : "Lösung wird angezeigt"}
                    </div>
                  ) : null}
                  {projection.jokerAnnouncement ? (
                    <div className="joker-announcement">{projection.jokerAnnouncement.message}</div>
                  ) : null}
                  {rankLeader ? (
                    <RankSpotlight
                      ranks={projection.ranks}
                      progress={rankValue(projection, rankLeader.score, rankLeader.rankProgress)}
                      unitLabel={projection.scoringMode === "points" ? "Punkte" : "Fortschritt"}
                      roomId={roomId}
                      token={token}
                    />
                  ) : null}
                  <h2>{projection.question.prompt}</h2>
                  <RoomImage
                    roomId={roomId}
                    token={token}
                    mediaId={projection.question.media.imageMediaId}
                    className={`question-media question-media--${projection.question.media.imageSize ?? "normal"}`}
                  />
                  <div className="answer-grid">
                    {projection.question.answers.map((answer) => (
                      <div
                        className={`answer-card ${answerPresentationClasses(
                          answer.optionId,
                          projection.hiddenOptionIds,
                          projection.answerMarks,
                          projection.solutionOptionIds,
                          projection.fiftyFiftyAnimation,
                          fiftyFiftyComplete,
                        )}`}
                        key={answer.optionId}
                      >
                        {answer.text}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="stream-waiting">Gleich geht es los</div>
              )}
            </div>
            </section>
            {projection.ranks.length ? (
              <RankLadder
                ranks={projection.ranks}
                entries={(projection.teams.length ? projection.teams : projection.players).map((entry) => ({
                  id: "name" in entry ? entry.teamId : entry.playerId,
                  label: "name" in entry ? entry.name : entry.displayName,
                  progress: rankValue(projection, entry.score, entry.rankProgress),
                  color: "color" in entry ? entry.color : undefined,
                }))}
                primaryEntryId={
                  rankLeader ? ("name" in rankLeader ? rankLeader.teamId : rankLeader.playerId) : undefined
                }
                unitLabel={projection.scoringMode === "points" ? "Punkte" : "Fortschritt"}
                roomId={roomId}
                token={token}
                compact
              />
            ) : null}
          </div>
          <div className={`stream-buzzer-status stream-buzzer-status--${projection.buzzer.status}`}>
            {winner
              ? `${winner.displayName} hat gebuzzert!`
              : projection.buzzer.status === "open"
                ? "Buzzer aktiv"
                : "Buzzer gesperrt"}
          </div>
          {projection.submissions?.length ? (
            <section className="stream-submissions">
              {projection.submissions.map((submission) => {
                const player = projection.players.find((entry) => entry.playerId === submission.playerId);
                return (
                  <div className={`stream-submission stream-submission--${submission.evaluation}`} key={submission.submissionId}>
                    <strong>{player?.displayName ?? "Teilnehmer"}</strong>
                    <span>{releasedAnswerText(projection, submission.optionIds, submission.text)}</span>
                    <small>
                      {submission.evaluation === "correct"
                        ? "Richtig"
                        : submission.evaluation === "incorrect"
                          ? "Falsch"
                          : submission.evaluation === "partial"
                            ? "Teilweise"
                            : "Freigegeben"}
                    </small>
                  </div>
                );
              })}
            </section>
          ) : null}
          <section className="stream-scoreboard">
            {(projection.teams.length ? projection.teams : projection.players)
              .slice()
              .sort((left, right) =>
                projection.scoringMode === "rank-progress"
                  ? right.rankProgress - left.rankProgress
                  : right.score - left.score,
              )
              .map((entry) => (
                <div
                  className={`scoreboard-entry ${"status" in entry && entry.status === "eliminated" ? "scoreboard-entry--eliminated" : ""}`}
                  key={"teamId" in entry ? entry.teamId : entry.playerId}
                >
                  {"logoMediaId" in entry ? (
                    <RoomImage
                      roomId={roomId}
                      token={token}
                      mediaId={entry.logoMediaId}
                      className="team-logo"
                    />
                  ) : null}
                  <strong>{"name" in entry ? entry.name : entry.displayName}</strong>
                  <ScoreStatus
                    projection={projection}
                    score={entry.score}
                    rankProgress={entry.rankProgress}
                    roomId={roomId}
                    token={token}
                  />
                  {projection.usesLives ? (
                    <small>
                      <LivesStatus projection={projection} lives={entry.lives} />
                    </small>
                  ) : null}
                  <JokerStatus
                    jokers={projection.jokers}
                    counts={entry.jokerCounts}
                    roomId={roomId}
                    token={token}
                  />
                </div>
              ))}
          </section>
          <footer className="stream-footer">
            A NexVerse Project <BuildInfo compact />
          </footer>
        </>
      ) : (
        <div className="stream-waiting">Stream wird verbunden...</div>
      )}
    </main>
  );
}

function StreamEnd({ projection }: { projection: NonNullable<ReturnType<typeof useRoomConnection>["projection"]> }) {
  const entries = [...(projection.teams.length ? projection.teams : projection.players)].sort((left, right) =>
    projection.scoringMode === "rank-progress"
      ? right.rankProgress - left.rankProgress
      : right.score - left.score,
  );
  return (
    <div className="stream-end">
      <span className="eyebrow">Finale</span>
      <h2>Danke fürs Mitspielen!</h2>
      <div className="stream-podium">
        {entries.slice(0, 3).map((entry, index) => (
          <div className={`stream-podium__place stream-podium__place--${index + 1}`} key={"teamId" in entry ? entry.teamId : entry.playerId}>
            <span>Platz {index + 1}</span>
            <strong>{"name" in entry ? entry.name : entry.displayName}</strong>
            <b>
              <ScoreStatus projection={projection} score={entry.score} rankProgress={entry.rankProgress} />
            </b>
          </div>
        ))}
      </div>
    </div>
  );
}
